/* Playthrough record — the per-game serialization format defined in
   briefs/playthrough.md (schema "palindragon-playthrough/v1").

   At launch the record is purely client-side: written to localStorage
   on the win/loss commit, read back when the same puzzle id loads
   again so the player sees their finished game instead of replaying.

   The wire shape is brief-canonical (turns array, hint as two
   consecutive entries) so a future server adjudicator can ingest it
   without translation. The in-memory game state (`guesses`,
   `hintPurchasedAt`, `status`) remains the runtime SoT — this module
   is the boundary between that and on-disk storage. */

import type { Puzzle, PuzzleAnalysis } from "./puzzle";
import { buildMask } from "./puzzle";
import type { Mark } from "./scoring";
import { scoreGuess } from "./scoring";

export const PLAYTHROUGH_SCHEMA = "palindragon-playthrough/v1";

export type GuessTurn = {
  type: "guess";
  /** Length = ceil(letters / 2). Unentered cells are "_". */
  half: string;
  /** Per-cell ASCII feedback, same length as `half`. Y/M/N → 🟩/🟨/⬛. */
  feedback: string;
};

export type HintTurn = { type: "hint" };

export type Turn = GuessTurn | HintTurn;

export type Outcome = "won" | "lost" | "abandoned";

export type Playthrough = {
  schema: typeof PLAYTHROUGH_SCHEMA;
  puzzle_number: number;
  date: string;
  mask: string;
  letters: number;
  max_guesses: number;
  turns: Turn[];
  outcome: Outcome;
  solution_normalized: string;
};

export type GuessRow = readonly (string | null)[];

const STORAGE_PREFIX = "palindragon-playthrough:";

function storageKey(puzzleId: number): string {
  return `${STORAGE_PREFIX}${puzzleId}`;
}

function halfLength(letterCount: number): number {
  return Math.ceil(letterCount / 2);
}

function markToFeedback(m: Mark): "Y" | "M" | "N" {
  if (m === "green") return "Y";
  if (m === "yellow") return "M";
  return "N"; // gray + empty both render N (per brief §"Feedback encoding")
}

function buildGuessTurn(
  guess: GuessRow,
  analysis: PuzzleAnalysis,
): GuessTurn {
  const sol = analysis.normalized.split("");
  const fullMarks = scoreGuess(guess, sol);
  const halfLen = halfLength(analysis.letterCount);
  let half = "";
  let feedback = "";
  for (let i = 0; i < halfLen; i++) {
    const ch = guess[i] ?? null;
    half += ch ?? "_";
    feedback += markToFeedback(fullMarks[i]!);
  }
  return { type: "guess", half, feedback };
}

/** Serialize the current finished-game state into a Playthrough.
   Returns null if the game is still in progress; the caller decides
   whether an in-progress save is meaningful (it isn't at launch —
   the brief requires "abandoned" for incomplete state, and we don't
   persist abandoned). */
export function buildPlaythrough(args: {
  puzzle: Puzzle;
  analysis: PuzzleAnalysis;
  guesses: readonly GuessRow[];
  hintPurchasedAt: number | null;
  status: "playing" | "won" | "lost";
}): Playthrough | null {
  const { puzzle, analysis, guesses, hintPurchasedAt, status } = args;
  if (status === "playing") return null;

  const turns: Turn[] = [];
  for (let g = 0; g < guesses.length; g++) {
    if (hintPurchasedAt != null && g === hintPurchasedAt) {
      turns.push({ type: "hint" }, { type: "hint" });
    }
    turns.push(buildGuessTurn(guesses[g]!, analysis));
  }
  // Hint purchased after the last guess (e.g., the player bought it on
  // the final remaining slot and immediately lost). Brief rule 7
  // permits this case.
  if (hintPurchasedAt != null && hintPurchasedAt >= guesses.length) {
    turns.push({ type: "hint" }, { type: "hint" });
  }

  return {
    schema: PLAYTHROUGH_SCHEMA,
    puzzle_number: puzzle.id,
    date: puzzle.date,
    mask: buildMask(puzzle.sentence),
    letters: analysis.letterCount,
    max_guesses: puzzle.maxGuesses,
    turns,
    outcome: status,
    solution_normalized: analysis.normalized,
  };
}

/** Mirror a half (length = ceil(letterCount/2)) into the full-length
   guess row. Returns null on length mismatch. Underscores become null. */
function halfToFullGuess(
  half: string,
  letterCount: number,
): (string | null)[] | null {
  if (half.length !== halfLength(letterCount)) return null;
  const out: (string | null)[] = new Array(letterCount).fill(null);
  for (let i = 0; i < half.length; i++) {
    const c = half[i]!;
    const value = c === "_" ? null : c;
    out[i] = value;
    out[letterCount - 1 - i] = value;
  }
  return out;
}

export type AppliedPlaythrough = {
  guesses: GuessRow[];
  hintPurchasedAt: number | null;
  status: "won" | "lost";
};

/** Decode a Playthrough into the signal-shaped state. Returns null
   when the record is malformed or doesn't match the supplied analysis
   (puzzle id collision with a different solution, schema drift, etc.).
   Validation here mirrors briefs/playthrough.md §"Validation rules"
   without the canonical re-derivation step (we trust scoreGuess to
   produce matching marks at render time). */
export function applyPlaythrough(
  p: Playthrough,
  analysis: PuzzleAnalysis,
): AppliedPlaythrough | null {
  if (p.schema !== PLAYTHROUGH_SCHEMA) return null;
  if (p.letters !== analysis.letterCount) return null;
  if (p.solution_normalized !== analysis.normalized) return null;
  if (p.outcome !== "won" && p.outcome !== "lost") return null;

  const restored: (string | null)[][] = [];
  let hintAt: number | null = null;
  let i = 0;
  while (i < p.turns.length) {
    const turn = p.turns[i]!;
    if (turn.type === "hint") {
      if (hintAt != null) return null; // multiple hint pairs
      if (i + 1 >= p.turns.length || p.turns[i + 1]!.type !== "hint") {
        return null; // unpaired hint
      }
      hintAt = restored.length;
      i += 2;
      continue;
    }
    const full = halfToFullGuess(turn.half, analysis.letterCount);
    if (full == null) return null;
    restored.push(full);
    i++;
  }

  return {
    guesses: restored,
    hintPurchasedAt: hintAt,
    status: p.outcome,
  };
}

/** Persist a finished playthrough to localStorage, keyed by puzzle id.
   Silent no-op when storage is unavailable (private mode, quota,
   disabled), matching the project's existing theme.ts pattern. The
   caller is responsible for not passing "abandoned" — at launch the
   brief excludes those records from persistence. */
export function savePlaythrough(p: Playthrough): void {
  if (p.outcome === "abandoned") return;
  try {
    localStorage.setItem(storageKey(p.puzzle_number), JSON.stringify(p));
  } catch {
    /* private mode / disabled storage — in-memory only */
  }
}

/** Load a previously-saved playthrough by puzzle id, or null if there
   isn't one (or the record is unreadable). Schema-mismatch records
   are treated as absent so a future v2 ships without breaking old
   players' stored runs — they just lose the historical view, which
   is acceptable. */
export function loadPlaythrough(puzzleId: number): Playthrough | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKey(puzzleId));
  } catch {
    return null;
  }
  if (raw == null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const candidate = parsed as Playthrough;
  if (candidate.schema !== PLAYTHROUGH_SCHEMA) return null;
  if (typeof candidate.puzzle_number !== "number") return null;
  if (!Array.isArray(candidate.turns)) return null;
  return candidate;
}

/** Erase a stored playthrough — exposed primarily for tests. */
export function clearPlaythrough(puzzleId: number): void {
  try {
    localStorage.removeItem(storageKey(puzzleId));
  } catch {
    /* same as save */
  }
}
